// utils
import React from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";

// components
import GrantInfo, { GrantInfoItem } from "../grant-info/grant-info";

export type GrantTileSide = "start" | "end";

export interface GrantTileProps {
    /** project name - single line header */
    header: string;
    /** short project description (optional - not rendered when omitted) */
    paragraph?: string;
    /** key grant data (operational programme, call, registration number, beneficiary ...) */
    info: GrantInfoItem[];
    /** co-financing statement required by the grant provider */
    cofinancedNote: string;
    /** co-financing banner (EU / ministry logos) - replaces project image */
    bannerSrc: string;
    bannerAlt: string;
    /** "read more" button */
    btnText: string;
    url: string;
    /**
     * zig-zag layout (same as OUR-WORK & BLOG tiles) - from 992px up the tile is shifted
     * "start" = offset from the left (blog-older-container-ms), "end" = offset from the right (blog-older-container-me)
     */
    side: GrantTileSide;
}

/**
 * GrantTile component
 * reusable tile presenting one grant project on GRANTS page (based on OUR-WORK project tiles)
 * @returns HTML code of one grant tile
 */
export default function GrantTile(props: GrantTileProps) {

    // solves broken layout of some pages in build
    const [hasMounted, setHasMounted] = React.useState(false);
    React.useEffect(() => {
      setHasMounted(true);
    }, []);
    if (!hasMounted) {
      return null;
    }

    const sideClass = props.side === "end" ? "blog-older-container-me" : "blog-older-container-ms";

    return (
        <>
            <div className="bg-white pb-5">
                <Container className="pt-0">
                    <Row className={"bg-white project-tile-box-shadow-divelit-grey-10 project-tile project-tile-border b-top-divelit-lemon-12 mt-5 p-3 p-lg-4 p-xl-5 scroll-anim-1000 " + sideClass}>

                        {/* co-financing banner (replaces project image) */}
                        <Col xs={12} sm={12} className="p-0 mt-3 mt-lg-2">
                            <div className="grants-eu-banner">
                                <img className="grants-eu-banner-img" src={props.bannerSrc} alt={props.bannerAlt}/>
                            </div>
                        </Col>

                        {/* project name (+ optional short description) */}
                        <Col xs={12} sm={12} className="p-0 mt-4 mt-lg-5">
                            <h4 className="font-black mb-0">
                                {props.header}
                            </h4>
                            {/* short description - optional (hidden on GRANTS page to keep tiles compact, full text is on detail page) */}
                            {props.paragraph ?
                                <p className="paragraph-small mt-3 mt-xl-4 mb-0">
                                    {props.paragraph}
                                </p>
                            : <></>}
                        </Col>

                        {/* key grant data */}
                        <Col xs={12} sm={12} className="p-0 mt-4 mt-lg-5">
                            <GrantInfo info={props.info}/>
                        </Col>

                        {/* co-financing statement + read more */}
                        <Col xs={12} sm={12} className="p-0 mt-4 mt-lg-5">
                            <hr className="sepparator-horiz-divelit-grey-10"></hr>
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center mt-3 mt-lg-4">
                                <p className="paragraph-extra-small font-divelit-grey-50 grants-cofinanced-note flex-grow-1 mb-3 mb-lg-0 me-lg-5">
                                    {props.cofinancedNote}
                                </p>
                                <div className="flex-shrink-0">
                                    <Link className="button-w-border paragraph-small" to={props.url}>
                                        {props.btnText}<img className="button-arrow" alt="" aria-hidden="true"></img>
                                    </Link>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
}
